import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { Document, Page, pdfjs } from "react-pdf";
import UploadedPOSA, { Status } from "@tootsweet/model/entities/UploadedPOSA";
import moment from "moment";

import KC_Button from "../../../../../../designsystem/KC_Button/KC_Button";
import POSAApi from "../../../../../12_POSAManager/api";
import { useAccountToDisplaySelector } from "../../../../../../redux/actions/user";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { setLoading } from "../../../../../../redux/actions/app";
import { tt } from "../../../../../../utils/i18n/i18n";
import CONFIG from "../../../../../../model/TSConfig";
import KC_Modal_2 from "../../../../../../designsystem/KC_Modal/KC_Modal_2";
import KC_Heading3 from "../../../../../../designsystem/typography/KC-Heading/KC_Heading3";
import "./styles.scss";

interface Props {
  uploadedPOSA: UploadedPOSA;
  onClose?: () => void;
  onRefresh?: () => Promise<void>;
}

const PreviewModal = NiceModal.create((props: Props) => {
  const dispatch = useDispatch();
  const { accountToDisplay } = useAccountToDisplaySelector();
  const modal = useModal();

  const [comment, setComment] = useState<string>("");
  const commentsEndRef = useRef<HTMLDivElement>(null);

  const url = `${CONFIG.s3BucketURL}/kadow/saas/customer_posa/${props.uploadedPOSA.trackingID}/${props.uploadedPOSA.type}_output`;

  const isDone = useMemo(() => {
    return props.uploadedPOSA.status === Status.DONE;
  }, [props.uploadedPOSA.status]);

  const hasComments = useMemo(() => {
    return props.uploadedPOSA.comments?.some((c) => !c.isInternal) ?? false;
  }, [props.uploadedPOSA.comments]);

  const handleClose = () => {
    props.onClose?.();
    modal.resolve(null);
    void modal.hide();
  };

  const handleSendComment = async () => {
    if (!accountToDisplay?.trackingID || !accountToDisplay?.token) {
      return;
    }

    try {
      dispatch(setLoading(true));
      await POSAApi.updateUploadedPOSA(
        accountToDisplay?.trackingID,
        {
          _id: props.uploadedPOSA._id,
          type: props.uploadedPOSA.type,
          comments: props.uploadedPOSA.comments
            ? [
                ...props.uploadedPOSA.comments,
                { comment, ts: moment().unix(), isInternal: false }
              ]
            : [{ comment, ts: moment().unix(), isInternal: false }],
          status: Status.RESENT
        },
        accountToDisplay?.token
      );
      props.onRefresh?.();
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(setLoading(false));
      handleClose();
    }
  };

  const handleValidate = async () => {
    if (!accountToDisplay?.trackingID || !accountToDisplay?.token) return;

    try {
      dispatch(setLoading(true));
      await POSAApi.updateUploadedPOSA(
        accountToDisplay?.trackingID,
        {
          type: props.uploadedPOSA.type,
          status: Status.DONE
        },
        accountToDisplay?.token
      );

      props.onRefresh?.();
      handleDownload();
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(setLoading(false));
      handleClose();
    }
  };

  const handleDownload = async () => {
    const response = await fetch(url, {
      cache: "no-store",
      headers: {
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache"
      }
    });
    const blob = await response.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${props.uploadedPOSA.type}_output.pdf`;
    link.target = "_blank";
    link.click();
    URL.revokeObjectURL(link.href);
    props.onClose?.();
  };

  useEffect(() => {
    if (commentsEndRef.current) {
      commentsEndRef.current.scrollIntoView();
    }
  }, [modal.visible, props.uploadedPOSA.comments]);

  return (
    <KC_Modal_2
      id="posa-preview-modal"
      onClose={handleClose}
      isOpen={modal.visible}
      header={<KC_Heading3 text={tt("posa_preview_modal_title")} />}
      footer={
        <div className="d-flex st-gap">
          {!isDone && (
            <KC_Button text={tt("validate_button")} onClick={handleValidate} />
          )}
          {isDone && (
            <KC_Button text={tt("download")} onClick={handleDownload} />
          )}
        </div>
      }
    >
      <div
        className="d-flex pt-2 pb-2 align-items-center justify-content-center pdf-scroll-container"
        style={{
          flex: "1 0 60%",
          overflowY: "auto",
          overflowX: "hidden",
          minWidth: 0
        }}
      >
        <Document file={url}>
          <Page pageNumber={1} className="d-flex justify-content-center" />
        </Document>
      </div>

      <div
        className="d-flex flex-column"
        style={{
          flex: "0 0 40%",
          borderLeft: "1px solid #dee2e6",
          padding: "16px"
        }}
      >
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            scrollbarWidth: "thin"
          }}
        >
          {hasComments &&
            [...props.uploadedPOSA.comments!]
              .filter((c) => !c.isInternal)
              .sort((a, b) => a.ts - b.ts)
              .map((c, index) => (
                <div
                  key={index}
                  className="mb-2 p-2"
                  style={{ backgroundColor: "#f5f5f5", borderRadius: "4px" }}
                >
                  <div style={{ fontWeight: "bold", marginBottom: "4px" }}>
                    {moment.unix(c.ts).format("DD MMMM YYYY, HH:mm")}
                  </div>
                  <div>{c.comment}</div>
                </div>
              ))}
          <div ref={commentsEndRef} />
        </div>

        {!isDone && (
          <div className="mt-2">
            <textarea
              name="comment"
              rows={3}
              className="form-control kc_input"
              onChange={(e) => setComment(e.target.value)}
              placeholder={tt("comment_placeholder")}
            />
            {comment && (
              <div className="d-flex justify-content-end mt-2">
                <KC_Button
                  text={tt("send_comment")}
                  onClick={handleSendComment}
                  disabled={!comment}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </KC_Modal_2>
  );
});

export default PreviewModal;
